/**
 * APTableHeader Component
 *
 * Table header section that manages the header row and all header cells.
 * Handles column visibility, sorting state, and accessibility.
 *
 * @example
 * ```tsx
 * <APTableHeader
 *   columns={[
 *     { key: 'name', label: 'Name', accessor: 'name', sortable: true },
 *     { key: 'email', label: 'Email', accessor: 'email' }
 *   ]}
 *   sort={{ column: 'name', direction: 'asc' }}
 *   onSortChange={(sort) => console.log(sort)}
 * />
 * ```
 */

import { forwardRef } from 'react';

import type { IAPTableColumn, IAPTableSelection, IAPTableSort } from '../../types';

import APTableHeaderCell from '../header-cell';

import './styles.scss';

import { useTranslate } from '@shared/hooks/use-translate';

export interface IAPTableHeaderProps<TData = Record<string, unknown>> {
  /**
   * Table columns configuration
   */
  columns: IAPTableColumn<TData>[];

  /**
   * Current sort configuration
   */
  sort?: IAPTableSort;

  /**
   * Sort change callback
   */
  onSortChange?: (sort: IAPTableSort | null) => void;

  /**
   * Table selection configuration
   */
  selection?: IAPTableSelection;

  /**
   * Additional CSS class names
   */
  className?: string;

  /**
   * Test identifier for testing
   */
  'data-testid'?: string;
}

/**
 * APTableHeader - Table header section
 *
 * Manages the header row and renders all header cells with
 * proper sorting and accessibility features.
 */
export const APTableHeader = forwardRef<HTMLTableSectionElement, IAPTableHeaderProps>(
  <TData,>(
    {
      columns,
      sort,
      onSortChange,
      selection,
      className = '',
      'data-testid': testId = 'ap-table-header',
      ...rest
    }: IAPTableHeaderProps<TData>,
    ref: React.Ref<HTMLTableSectionElement>
  ) => {
    const translate = useTranslate();
    // Filter visible columns
    const visibleColumns = columns.filter((column) => column.visible !== false);

    // Build header classes
    const headerClasses = ['ap-table__header', className].filter(Boolean).join(' ');

    return (
      <thead ref={ref} className={headerClasses} data-testid={testId} {...rest}>
        <tr className="ap-table__header-row">
          {/* Selection column header */}
          {selection !== undefined &&
            selection?.type !== 'none' &&
            selection?.showCheckboxes !== false && (
              <APTableHeaderCell
                column={{
                  key: '__selection__',
                  label: selection?.showSelectAll === true ? translate('table.selectAll') : '',
                  accessor: () => null,
                  align: 'center',
                  headerClassName: 'ap-table__header-cell--selection',
                }}
                sort={sort}
                onSortChange={onSortChange}
              />
            )}

          {/* Column headers */}
          {visibleColumns.map((column) => (
            <APTableHeaderCell
              key={column.key}
              column={column}
              sort={sort}
              onSortChange={onSortChange}
            />
          ))}
        </tr>
      </thead>
    );
  }
) as <TData = Record<string, unknown>>(
  props: IAPTableHeaderProps<TData> & {
    ref?: React.Ref<HTMLTableSectionElement>;
  }
) => React.ReactElement;

export default APTableHeader;
